import {
  getAnchorPositions,
  getAnchorRanges,
  getWordSpans,
  parseScript,
  serializeScript,
} from "pages/media/helpers/scriptAnchors/";
import { ParsedScript } from "pages/media/helpers/types/";
import { usesWordSegmenter } from "helpers/utils";
import { EMO_COLORS } from "../constants";

// Includes Chinese full-width punctuation (，。！？；：、 and closing brackets).
const PUNCTUATION_RE = /^[.,!?;:'"()\-–—…،؛؟，。！？；：、」』）》〉】〕]+$/;
const SENTENCE_END_CHARS = new Set([".", "!", "?", "؟", "。", "！", "？"]);
const PUNCT_CHARS = new Set(".,!?;:'\"()-–—…،؛؟，。！？；：、」』）》〉】〕");
const CJK_CLOSING_BRACKETS = new Set("」』）》〉】〕");

export interface WordToken {
  word: string;
  index: number;
  highlightId?: string;
  highlightIncomplete?: "start" | "end";
  pronunciation?: string;
  isPunctuation?: boolean;
  emoId?: string;
  /**
   * Whether the original text has whitespace before this token. Only set for
   * languages without spaces between words (Chinese); otherwise derived by
   * `hasSpaceBefore`.
   */
  spaceBefore?: boolean;
}

/** Whether a space goes before the token at `index` when rendering/saving. */
export const hasSpaceBefore = (t: WordToken, index: number) =>
  t.spaceBefore ?? (index > 0 && !t.isPunctuation);

function splitWordAndPunctuation(text: string): string[] {
  let i = text.length;
  while (i > 0 && PUNCT_CHARS.has(text[i - 1])) i--;
  if (i < text.length) {
    const word = text.slice(0, i);
    return word ? [word, text.slice(i)] : [text];
  }
  return [text];
}

// "。」" still ends a sentence.
const lastSentenceChar = (word: string) => {
  let end = word.length;
  while (end > 0 && CJK_CLOSING_BRACKETS.has(word.charAt(end - 1))) end--;
  return word.charAt(end - 1);
};

function isSentenceEndToken(t: WordToken) {
  return !!t.isPunctuation && SENTENCE_END_CHARS.has(lastSentenceChar(t.word));
}

/**
 * Convert a ParsedScript to WordToken[] for the Emotions/Pronunciation editors.
 * Each word gets an incrementing index. EMO markers set `emoId` on subsequent
 * words until sentence end. HL/PRN ranges annotate words within them.
 */
export function parsedToWordTokens(
  parsed: ParsedScript,
  language?: string
): WordToken[] {
  const { text } = parsed;
  const tokens: WordToken[] = [];
  const wordSpans = getWordSpans(text, language);
  const keepOriginalSpacing = usesWordSegmenter(language);
  const hlRanges = getAnchorRanges(parsed, "HL");
  const prnRanges = getAnchorRanges(parsed, "PRN");
  const emos = [...getAnchorPositions(parsed, "EMO")].sort(
    (a, b) => a.position - b.position
  );

  let currentEmoId: string | undefined;
  let nextEmoIdx = 0;
  let wordIndex = 0;

  for (const [spanIdx, span] of wordSpans.entries()) {
    const previousEnd = wordSpans[spanIdx - 1]?.end ?? 0;
    while (
      nextEmoIdx < emos.length &&
      emos[nextEmoIdx]?.position <= span.start
    ) {
      currentEmoId = emos[nextEmoIdx].id;
      nextEmoIdx++;
    }

    const hl = hlRanges.find(
      (r) => span.start >= r.start && span.start < r.end
    );
    const prn = prnRanges.find(
      (r) => span.start >= r.start && span.start < r.end
    );

    for (const [partIdx, part] of splitWordAndPunctuation(span.raw).entries()) {
      const token: WordToken = {
        word: part,
        index: wordIndex++,
        isPunctuation: PUNCTUATION_RE.test(part),
        emoId: currentEmoId,
        highlightId: hl?.id,
        highlightIncomplete: hl?.orphan,
        pronunciation: prn?.id,
      };
      if (keepOriginalSpacing) {
        token.spaceBefore =
          spanIdx > 0 && partIdx === 0 && span.start > previousEnd;
      }
      tokens.push(token);
      if (isSentenceEndToken(token)) currentEmoId = undefined;
    }
  }

  return tokens;
}

/**
 * Convert WordToken[] back to a bracket-notation string.
 * Pass `originalParsed` to re-insert SLIDE markers (not represented in WordToken).
 */
export function wordTokensToScript(
  tokens: WordToken[],
  originalParsed?: ParsedScript
): string {
  const parts: string[] = [];
  let currentHlId: string | undefined;
  let currentHlIncomplete: "start" | "end" | undefined;
  let currentPrnId: string | undefined;
  let lastEmoId: string | undefined;

  const flushHl = () => {
    if (
      currentHlId &&
      (!currentHlIncomplete || currentHlIncomplete === "end")
    ) {
      parts.push(`[[/HL-${currentHlId}]]`);
    }
    currentHlId = undefined;
    currentHlIncomplete = undefined;
  };

  const flushPrn = () => {
    if (currentPrnId) parts.push(`[[/PRN-${currentPrnId}]]`);
    currentPrnId = undefined;
  };

  for (const [i, t] of tokens.entries()) {
    const space = hasSpaceBefore(t, i);

    if (t.emoId && t.emoId !== lastEmoId) parts.push(`[[EMO-${t.emoId}]]`);
    if (isSentenceEndToken(t)) lastEmoId = undefined;
    else if (t.emoId) lastEmoId = t.emoId;

    const hlChanged = t.highlightId !== currentHlId;
    const prnChanged = t.pronunciation !== currentPrnId;
    // PRN groups consecutive same-id tokens that have no whitespace between
    // them (e.g. word + trailing punctuation). A space ends the current group.
    const prnBoundary = !!currentPrnId && space;

    // Close in reverse-nesting order: PRN sits inside HL.
    if (prnChanged || prnBoundary) flushPrn();
    if (hlChanged) flushHl();

    if (t.highlightId && hlChanged) {
      if (space) parts.push(" ");
      if (!t.highlightIncomplete || t.highlightIncomplete === "start") {
        parts.push(`[[HL-${t.highlightId}]]`);
      }
      currentHlId = t.highlightId;
      currentHlIncomplete = t.highlightIncomplete;
    } else if (space) {
      parts.push(" ");
    }

    if (t.pronunciation && !currentPrnId) {
      parts.push(`[[PRN-${t.pronunciation}]]`);
      currentPrnId = t.pronunciation;
    }

    parts.push(t.word);
  }

  flushPrn();
  flushHl();
  const result = parts.join("");

  // Re-insert SLIDE markers from original
  if (originalParsed) {
    const slides = originalParsed.markers.filter((m) => m.tag === "SLIDE");
    if (slides.length > 0) {
      const rp = parseScript(result);
      return serializeScript({
        text: rp.text,
        markers: [
          ...rp.markers,
          ...slides.map((m) => ({
            ...m,
            position: Math.min(m.position, rp.text.length),
          })),
        ],
      });
    }
  }

  return result;
}

export const buildPrnAnchor = (
  pronunciation: string,
  content: string
): string => `[[PRN-${pronunciation}]]${content}[[/PRN-${pronunciation}]]`;

export const removeEmoFromTokens = (
  tokens: WordToken[],
  startIdx: number,
  endIdx: number
): WordToken[] =>
  tokens.map((t, i) =>
    i >= startIdx && i <= endIdx ? { ...t, emoId: undefined } : t
  );

export const getEmoColor = (emoId: string): string | undefined =>
  EMO_COLORS.find((e) => e.id === emoId)?.color;

export const setEmoOnTokens = (
  tokens: WordToken[],
  startIdx: number,
  endIdx: number,
  emoId: string
): WordToken[] =>
  tokens.map((t, i) => (i >= startIdx && i <= endIdx ? { ...t, emoId } : t));

export const isSentenceEnd = isSentenceEndToken;

export const getSentenceIndicesForToken = (
  tokens: WordToken[],
  tokenIdx: number
): { start: number; end: number } => {
  const token = tokens[tokenIdx];
  if (!token.emoId) return { start: tokenIdx, end: tokenIdx };

  let start = tokenIdx;
  let end = tokenIdx;

  while (start > 0 && tokens[start - 1].emoId === token.emoId) {
    start--;
  }

  while (end < tokens.length - 1) {
    const next = tokens[end + 1];
    if (next.emoId !== token.emoId) break;
    end++;
    if (isSentenceEnd(next)) break;
  }

  return { start, end };
};

export const getSentenceForToken = (
  tokens: WordToken[],
  tokenIdx: number
): WordToken[] => {
  const { start, end } = getSentenceIndicesForToken(tokens, tokenIdx);
  return tokens.slice(start, end + 1);
};

export const getSentenceFromTokenToEnd = (
  tokens: WordToken[],
  tokenIdx: number
): WordToken[] => {
  const { start, end } = getSentenceIndicesFromTokenToEnd(tokens, tokenIdx);
  return tokens.slice(start, end + 1);
};

export const getSentenceIndicesFromTokenToEnd = (
  tokens: WordToken[],
  tokenIdx: number
): { start: number; end: number } => {
  let end = tokenIdx;
  while (end < tokens.length - 1) {
    if (isSentenceEnd(tokens[end])) break;
    if (tokens[end + 1].emoId) break;
    end++;
  }
  return { start: tokenIdx, end };
};

export const renderSentence = (sentenceTokens: WordToken[]) =>
  sentenceTokens
    .map((t, i) => (i > 0 && hasSpaceBefore(t, i) ? " " + t.word : t.word))
    .join("");
